/** Pure appearance policy. Edit config/visual_themes.json to customize a whole pack. */
import source from "../../config/visual_themes.json";

export type AppearanceThemeId = "neighborhood" | "realism" | "cartoon" | "graybox";
export type ThemeRgb = readonly [number, number, number];
export const DEFAULT_APPEARANCE_THEME: AppearanceThemeId = "neighborhood";
export interface AppearanceTheme {
  id: AppearanceThemeId;
  version: string;
  label: string;
  description: string;
  policy: { externalModels: boolean; photographicSurfaces: boolean; photographicSky: boolean; photographicVegetation: boolean; splatWorld: boolean; expressiveRobot: boolean };
  palette: Record<string, ThemeRgb>;
  colorTreatment: { saturation: number; brightness: number; tint: ThemeRgb };
  material: { specular: number; roughness: number };
  categories: Record<string, { style: AppearanceThemeId; fallback: string }>;
  textures: Record<string, string | null>;
  models: Record<string, string | null>;
  lighting: Record<"day" | "dusk" | "night", { sky: ThemeRgb; ambient: number; sun: number; exposure: number; contrast: number }>;
  ui: { background: string; surface: string; text: string; accent: string; border: string; radius: string };
}
export interface AppearanceManifest {
  theme_id: AppearanceThemeId;
  theme_version: string;
  config_hash: string;
  assets: "loading" | "ready" | "degraded";
  failed_assets: string[];
  fallback_policy: "same-theme-procedural";
}
export function isAppearanceThemeId(value: unknown): value is AppearanceThemeId {
  return value === "neighborhood" || value === "realism" || value === "cartoon" || value === "graybox";
}
export function normalizeAppearanceTheme(value: unknown): AppearanceThemeId {
  return isAppearanceThemeId(value) ? value : DEFAULT_APPEARANCE_THEME;
}
/** Reject cross-style bindings rather than silently constructing a mixed pack. */
export function validateAppearanceThemes(value: unknown): asserts value is { schema: string; themes: AppearanceTheme[] } {
  const data = value as typeof source;
  if (data?.schema !== "streetsense.visual-themes/1" || !Array.isArray(data.themes)) throw new Error("Invalid visual theme document");
  const ids = new Set<string>();
  for (const theme of data.themes) {
    if (!isAppearanceThemeId(theme.id) || ids.has(theme.id)) throw new Error("Invalid or duplicate visual theme id");
    ids.add(theme.id);
    if (!/^\d+\.\d+\.\d+$/.test(theme.version)) throw new Error(`Invalid version for ${theme.id}`);
    for (const category of ["robot", "people", "vehicles", "vegetation", "buildings", "road", "props", "sky", "ui", "infrastructure", "items"]) {
      const slot = (theme.categories as Record<string, { style: string; fallback: string }>)[category];
      if (!slot || slot.style !== theme.id || !slot.fallback) throw new Error(`${theme.id}: missing or mixed ${category} style`);
    }
    for (const role of ["robot", "people", "vehicles", "vegetation", "buildings", "road", "ground", "props", "sky", "accent", "infrastructure", "items"]) {
      if (!(theme.palette as Record<string, unknown>)[role]) throw new Error(`${theme.id}: missing ${role} palette`);
    }
    for (const key of ["externalModels", "photographicSurfaces", "photographicSky", "photographicVegetation", "splatWorld", "expressiveRobot"] as const) if (typeof theme.policy?.[key] !== "boolean") throw new Error(`${theme.id}: invalid policy flag`);
    for (const value of [theme.colorTreatment.saturation, theme.colorTreatment.brightness, theme.material.specular, theme.material.roughness]) {
      if (!Number.isFinite(value) || value < 0 || value > 3) throw new Error(`${theme.id}: invalid material/color setting`);
    }
    for (const hour of ["day", "dusk", "night"] as const) {
      const lighting = theme.lighting[hour];
      if (!lighting || [lighting.ambient, lighting.sun, lighting.exposure, lighting.contrast].some(v => !Number.isFinite(v) || v < 0 || v > 5)) throw new Error(`${theme.id}: invalid ${hour} lighting`);
    }
    for (const key of ["background", "surface", "text", "accent", "border"] as const) if (!/^#[\da-f]{6}$/i.test(theme.ui[key])) throw new Error(`${theme.id}: invalid UI color`);
    if (!/^\d{1,2}px$/.test(theme.ui.radius)) throw new Error(`${theme.id}: invalid UI radius`);
    if (!theme.textures["blobshadow.png"]) throw new Error(`${theme.id}: the generated shadow mask is required`);
    for (const rgb of [...Object.values(theme.palette), theme.colorTreatment.tint, ...Object.values(theme.lighting).map(l => l.sky)]) {
      if (rgb.length !== 3 || rgb.some(c => !Number.isFinite(c) || c < 0 || c > 1)) throw new Error(`${theme.id}: invalid RGB color`);
    }
    if ((theme.id === "cartoon" || theme.id === "graybox") && (theme.policy.externalModels || theme.policy.photographicSurfaces || theme.policy.photographicSky || theme.policy.photographicVegetation || theme.policy.splatWorld || Object.values(theme.models).some(Boolean) || Object.entries(theme.textures).some(([key, path]) => key !== "blobshadow.png" && path))) {
      throw new Error(`${theme.id}: photographic assets would mix styles`);
    }
    if (theme.id === "neighborhood") {
      // Approved CC0 PBR surface scans are scoped to the Adventure alley benchmark.
      if (theme.policy.photographicSky || theme.policy.photographicVegetation || theme.policy.splatWorld) throw new Error("neighborhood: photographic policies would mix styles");
      for (const [kind, bindings] of [["textures", theme.textures], ["models", theme.models]] as const) {
        for (const [key, path] of Object.entries(bindings)) {
          if (path && !(kind === "textures" && key === "blobshadow.png" && path === "textures/blobshadow.png") && !path.startsWith(kind + "/neighborhood/")) throw new Error("neighborhood: asset ownership would mix styles");
        }
      }
    }
    for (const path of [...Object.values(theme.textures), ...Object.values(theme.models)]) {
      if (path && (!/^(textures|models)\/[\w./-]+$/.test(path) || path.includes(".."))) throw new Error(`${theme.id}: invalid asset path`);
    }
  }
  if (ids.size !== 4) throw new Error("All four complete appearance packs are required");
}
validateAppearanceThemes(source);
export const APPEARANCE_THEMES: readonly AppearanceTheme[] = source.themes as unknown as AppearanceTheme[];
export function appearanceTheme(id: AppearanceThemeId): AppearanceTheme {
  return APPEARANCE_THEMES.find(theme => theme.id === id)!;
}
export function appearanceConfigHash(theme: AppearanceTheme): string {
  let hash = 0x811c9dc5;
  for (const char of JSON.stringify(theme)) hash = Math.imul(hash ^ char.charCodeAt(0), 0x01000193);
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}
export function themeAssetPath(id: AppearanceThemeId, kind: "models" | "textures", name: string): string | null {
  return appearanceTheme(id)[kind][name] ?? null;
}
/** Global color treatment reaches every legacy procedural part; explicit roles give
 * roads, vegetation and key actors independent pack-owned base colors. */
export function themeColor(id: AppearanceThemeId, rgb: ThemeRgb, role?: string): ThemeRgb {
  const theme = appearanceTheme(id);
  const base = role && theme.palette[role] ? theme.palette[role]! : rgb;
  const mean = (base[0] + base[1] + base[2]) / 3;
  const { saturation, brightness, tint } = theme.colorTreatment;
  return base.map((v, i) => Math.max(0.025, Math.min(1, (mean + (v - mean) * saturation) * brightness * tint[i]!))) as unknown as ThemeRgb;
}
